import { TaskListParser } from "@/components/chat/TaskListParser";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Upload } from "lucide-react";
import { useState } from "react";

const SAMPLE_TEXT = `✅ Tabbed Analysis Interface - Phase 1 Complete

What's Been Implemented
1. **New Component: AnalysisEditorTabs**
Created AnalysisEditorTabs.tsx with 5 tabs:

- [x] Current Tab
- [x] Synthesis Tab
- [x] Key Points Tab
- [x] Original Tab
- [x] Raw/Debug Tab

2. **Modified: AnalysisPanel**
Added state for synthesisContent, keyPoints, contextStatements
Integrated AnalysisEditorTabs component
Replaced single PrettyEditor with tabbed interface
Maintained all existing functionality

3. **Modified: CurrentFocusContainer**
Removed SynthesisPanel completely (138 lines removed)
Simplified to show only AnalysisPanel at full height
Removed synthesis-related props and handlers

What Still Needs Implementation
Next Steps for Full Feature:

Context Extraction Logic
- Extract declarative statements from Original content
- Extract declarative statements from Synthesis
- Combine with ContextContainer data
- Auto-populate contextStatements array

Key Points Extraction
- Extract key points from content
- Auto-populate keyPoints array
- Consider AI-powered extraction

ContextContainer Component
- Create new ContextContainer component
- Implement mechanism to pass context between Context tab and ContextContainer
- Store broader context separate from analysis-specific context

Synthesis Generation
- Hook up synthesis generation to populate Synthesis tab
- Update handleSynthesize to set synthesisContent state

Persistence
- Update memento save to include synthesis, keyPoints, context
- Update memento load to restore tab data`;

export const TaskListDemo = () => {
  const [taskText, setTaskText] = useState(SAMPLE_TEXT);

  const handleTasksChange = (
    tasks: Array<{
      id: string;
      text: string;
      completed: boolean;
      level: number;
      isHeader: boolean;
    }>
  ) => {
    console.log("Tasks updated:", tasks);
  };

  const exportTasks = () => {
    const blob = new Blob([taskText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tasks.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Task List Parser Demo</h1>
        <p className="text-muted-foreground">
          Convert markdown-style lists into interactive checkable tasks
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Input Text</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportTasks}>
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
          <textarea
            className="w-full h-[600px] p-3 border rounded-md font-mono text-sm resize-none"
            value={taskText}
            onChange={(e) => setTaskText(e.target.value)}
            placeholder="Paste your task list here..."
          />
        </Card>

        {/* Output */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b bg-muted/30">
            <h2 className="text-lg font-semibold">Task Checklist</h2>
          </div>
          <div className="h-[600px]">
            <TaskListParser text={taskText} onTasksChange={handleTasksChange} />
          </div>
        </Card>
      </div>

      <Card className="mt-6 p-4 bg-muted/30">
        <h3 className="font-semibold mb-2">Supported Formats:</h3>
        <ul className="text-sm space-y-1 text-muted-foreground">
          <li>
            • Markdown checkboxes:{" "}
            <code className="bg-muted px-1 rounded">- [ ] Task</code> or{" "}
            <code className="bg-muted px-1 rounded">- [x] Completed</code>
          </li>
          <li>
            • Bullet lists:{" "}
            <code className="bg-muted px-1 rounded">- Task</code> or{" "}
            <code className="bg-muted px-1 rounded">* Task</code>
          </li>
          <li>
            • Numbered lists:{" "}
            <code className="bg-muted px-1 rounded">1. Task</code>
          </li>
          <li>
            • Headers: <code className="bg-muted px-1 rounded">## Section</code>{" "}
            or <code className="bg-muted px-1 rounded">**Bold Text:**</code>
          </li>
          <li>
            • Completed sections:{" "}
            <code className="bg-muted px-1 rounded">✅ Completed Section</code>
          </li>
        </ul>
      </Card>
    </div>
  );
};
